/* globals window */
'use strict';

import router from './router';
import {manager as msgManager} from './messages';
import {exception as se} from './sink';

const DEFAULT_TIMEOUT = 10000;

/**
 * Returns a debounced version of `func`.
 *
 * If the function gets called while the async state is not resolved or rejected, then the call
 * is postponed until the state changes or the timeout arises. Only the last postponed call is made,
 * the other ones are forgotten.
 *
 * Use case: disable a button as long as its action is not complete.
 *
 * @param func Function to debounce
 * @param timeout Timeout in milliseconds
 * @returns {Function} Debounced version of `func`
 */
export function asyncDebounce(func, timeout = DEFAULT_TIMEOUT) {
    let start;
    let next;
    let watchdog;
    let promise;

    return function (...args) {
        const self = this;
        const now = (new Date()).getTime();

        function reset() {
            start = undefined;
            promise = undefined;

            if (watchdog) {
                clearTimeout(watchdog);
                watchdog = undefined;
            }

            if (next) {
                call(next.args);
                next = undefined;
            }
        }

        function call(callArgs) {
            start = now;

            promise = new Promise((resolve, reject) => {
                func.apply(self, callArgs).then((...promArgs) => {
                    reset();
                    resolve(...promArgs);
                }, (...promArgs) => {
                    reset();
                    reject(...promArgs);
                });

                watchdog = setTimeout(() => {
                    reset();
                    reject('timeout');
                }, timeout);
            });
        }

        if (!start || now - start >= timeout) {
            call(args);
        } else {
            next = {args};
        }

        return promise;
    };
}

/**
 * Returns a debounced version of `func`, and sets the `prop` of `this` to `true` while the action
 * is being completed (then sets it back to `false`).
 *
 * Global actions count is stored in `window.__actions_counter`, which is an object with `started`
 * and `done` properties. This is to facilitate unit testing.
 *
 * @param prop Property of `this` to hold the current action state
 * @param func Function to debounce
 * @param timeout Timeout in milliseconds
 * @returns {Function}
 */
export function asyncAction(prop, func, timeout = DEFAULT_TIMEOUT) {
    const dFunc = asyncDebounce(func, timeout);

    return function (...args) {
        const ac = window.__actions_counter || {
            started: 0,
            done: 0
        };

        if (window && !window.__actions_counter) {
            window.__actions_counter = ac;
        }

        this[prop] = true;
        ac.started += 1;

        /**
         * This function waits a bit and then marks the current task as done.
         *
         * Process synchronization based on timing is a terrible idea, however even using
         * `Vue.nextTick()` a dozen times doesn't work and I ain't got all day for this shit.
         */
        function markDone() {
            setTimeout(() => {
                ac.done += 1;
            }, 100);
        }

        return dFunc.apply(this, args).then((x) => {
            this[prop] = false;
            markDone();
            return x;
        }, (e) => {
            this[prop] = false;
            markDone();
            se(e);
            msgManager.push(
                'Erreur.',
                'Tout ne s\'est pas déroulé comme prévu. Merci d\'essayer à nouveau plus tard.',
                'danger'
            );
        });
    };
}

/**
 * Sleep `t` milliseconds if `await`-ed.
 *
 * @param t
 * @returns {Promise}
 */
export function sleep(t) {
    return new Promise((resolve) => {
        setTimeout(resolve, t);
    });
}

/**
 * Iterates over `it` and for each item yield an object with the `idx` of the `item` and the `item`
 * itself.
 *
 * The concept implemented in this method is fictitious. No identification with actual Python
 * builtins is intended or should be inferred.
 *
 * @param it An iterator
 */
export function * enumerate(it) {
    let idx = 0;

    for (const item of it) {
        yield {idx, item};
        idx += 1;
    }
}

/**
 * Iterate over an object's key/values
 *
 * @param obj
 */
export function * items(obj) {
    for (const k in obj) {
        if (obj.hasOwnProperty(k)) {
            yield [k, obj[k]];
        }
    }
}

/**
 * Generic function to create a function that updates a paginated list from an API to the `this`
 * object.
 *
 * @param loading
 * @param page
 * @param hasNext
 * @param list
 * @param fetch
 * @returns {Function}
 */
export function makeUpdateList({loading, page, hasNext, list, fetch}) {
    return asyncAction(loading, async function (nextPage = false) {
        const self = this;

        if (!nextPage) {
            self[page] = 1;
            const stuffList = await fetch.call(self, self[page]);
            self[hasNext] = !!stuffList.next;
            self[list] = stuffList.results;
        } else {
            self[page] += 1;
            const stuffList = await fetch.call(self, self[page]);
            self[hasNext] = !!stuffList.next;
            self[list].push(...stuffList.results);
        }
    });
}

/**
 * Executes the input (async) function and route to 'not-found' if it raises any exception
 *
 * @param func
 */
export function tryOr404(func) {
    return async function () {
        const self = this;

        try {
            return await func.apply(self, arguments);
        } catch (e) {
            se(e);
            router.replace({name: 'not-found'});
        }
    };
}

/**
 * Detect the appropriate file size unit
 *
 * @param s
 * @param reach
 */
export function smartSize(s, reach = 0.9) {
    const sizes = {
        'Pio': Math.pow(1024, 5),
        'Tio': Math.pow(1024, 4),
        'Gio': Math.pow(1024, 3),
        'Mio': Math.pow(1024, 2),
        'Kio': 1024,
        'octet': 0
    };

    for (const [k, v] of items(sizes)) {
        if (s >= v * reach) {
            const disp = s / v;
            return `${disp.toPrecision(4).replace('.', ',')}\u00a0${k}`;
        }
    }
}

/**
 * Evaluate any expression while catching its exceptions. If the exception throws exceptions or
 * returns undefined, the provided `def` value is returned instead.
 *
 * @param cb
 * @param def
 * @returns {*}
 */
export function d(cb, def = undefined) {
    try {
        const out = cb();

        if (out === undefined) {
            return def;
        } else {
            return out;
        }
    } catch (e) {
        return def;
    }
}

/**
 * Caches a function that returns a promise into the given object. If the result is already known,
 * resolves immediately.
 *
 * @param func
 * @param cache
 * @param key
 * @returns {Function}
 */
export function cachePromise(func, cache, key) {
    return async function () {
        if (!cache[key]) {
            cache[key] = func();
        }

        if (cache[key].then) {
            cache[key] = await cache[key];
        }

        return cache[key];
    };
}

/**
 * Compare two strings like a C function would do. Undefined is greater than any string.
 *
 * @param a
 * @param b
 * @returns {number}
 */
export function cmpStr(a, b) {
    if (a === b) {
        return 0;
    }

    if (a === undefined) {
        return 1;
    }

    if (b === undefined) {
        return -1;
    }

    if (a > b) {
        return 1;
    } else if (a < b) {
        return -1;
    } else {
        // Due to Javascript logic, this might not be entirely true
        return 0;
    }
}

/**
 * Returns the current time in milliseconds
 *
 * @returns {number}
 */
export function time() {
    return (new Date()).getTime();
}

/**
 * Debounces a setter by preferring an arbitrary "sticky" value.
 *
 * If the `stickTo` value is set, then it is set instantly. Otherwise, it will take the last set
 * value provided that `interval` time has passed since the last sticky value was set.
 *
 * Useful to filter the noise from drag{enter,over,leave}.
 *
 * @param setter
 * @param stickTo
 * @param interval
 * @returns {Function}
 */
export function debounceStickValue(setter, stickTo, interval) {
    let realValue;
    let lastPropagate;
    let lastStick;

    function propagate(v) {
        if (v !== lastPropagate) {
            lastPropagate = v;
            setter(v);
        }
    }

    function performCheck(now) {
        if (lastStick + interval > now) {
            propagate(stickTo);
        } else {
            propagate(realValue);
        }
    }

    return function (v) {
        const now = time();
        realValue = v;

        if (v === stickTo) {
            lastStick = now;
        }

        performCheck(now);
        setTimeout(() => {
            performCheck(now + interval);
        }, interval);
    };
}

export function undefineNull(obj) {
    if (typeof obj === 'object' && !(obj instanceof Array) && obj !== null) {
        const out = {};

        for (const [k, v] of items(obj)) {
            out[k] = undefineNull(v);
        }

        return out;
    } else if (obj === null) {
        return undefined;
    } else {
        return obj;
    }
}

export function mailTpl(ctx, str) {
    for (const [k, v] of items(ctx)) {
        str = str.replace(`[[${k.toUpperCase()}]]`, v);
    }
    return str;
}

export function deepCopy(obj) {
    if (typeof obj === 'object') {
        if (obj instanceof Array) {
            return obj.map(deepCopy);
        } else if (obj === null) {
            return null;
        } else {
            const out = {};

            for (const [k, v] of items(obj)) {
                out[k] = deepCopy(v);
            }

            return out;
        }
    } else {
        return obj;
    }
}

/**
 * Escapes a string into a HTML-safe content
 *
 * @param s {string} string to escape
 * @returns {string} escaped string
 */
export function escape(s) {
    const t = document.createElement('DIV');
    t.textContent = s;
    return t.innerHTML;
}

/**
 * Shorten and format the life plans list
 */
export function lifePlans(client) {
    const plans = client && client.life_plans;

    if (!plans) {
        return [];
    }

    const out = [];
    let i;

    for (i = 0; i < Math.min(2, plans.length); i += 1) {
        out.push(plans[i].slice(0, 4) + '.');
    }

    if (i < plans.length) {
        out.push(`+${plans.length - i}`);
    }

    return out;
}

/**
 * Return a link to google maps from an address
 * @param sourceJson
 * @returns {string}
 */
export function getAddressLink(address) {
    return `https://www.google.com/maps/place/${encodeURIComponent(address)}`;
}



// WEBPACK FOOTER //
// ./src/utils.js